import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAdmin } from 'dashboard/composables/useAdmin';

// SiteUp V2 — Dock pessoal de atalhos (mockup 08, aprovado 2026-08-08;
// implementação decidida 2026-08-10). "Seu dock é só seu": a configuração vive
// em ui_settings do USUÁRIO, não da conta.
//
// Um item é sempre { kind, key, label?, icon?, to?, adminOnly? }:
//   kind 'destination' → tela do sistema (Agenda, Configurações, …)
//   kind 'conversation' → atalho pra uma conversa/grupo específico, criado
//                          arrastando o card da lista pro dock
// `key` é único dentro do dock e é o que dedupe usa.

export const DOCK_SETTINGS_KEY = 'shortcut_dock';
export const CONVERSATION_DRAG_TYPE = 'application/x-siteup-conversation';

// Catálogo do "+": destinos que o sistema já tem. `to` é uma rota nomeada
// resolvida com o accountId corrente — nada de URL montada na mão.
export const DOCK_CATALOG = [
  // Agenda não é rota própria: é a view de agenda do Kanban (KanbanView
  // resolve `?view=agenda`). Apontar pra uma rota inventada daria 404.
  {
    key: 'agenda',
    icon: 'i-ri-calendar-2-line',
    route: 'kanban_board',
    query: { view: 'agenda' },
  },
  {
    key: 'settings',
    icon: 'i-ri-settings-3-line',
    route: 'general_settings_index',
  },
  { key: 'kanban', icon: 'i-ri-kanban-view', route: 'kanban_board' },
  { key: 'tasks', icon: 'i-ri-checkbox-line', route: 'tasks' },
  {
    key: 'contacts',
    icon: 'i-ri-contacts-line',
    route: 'contacts_dashboard_index',
  },
  {
    key: 'reports',
    icon: 'i-ri-bar-chart-line',
    route: 'account_overview_reports',
  },
  {
    key: 'captain',
    icon: 'i-ri-sparkling-line',
    route: 'captain_assistants_index',
  },
  {
    key: 'campaigns',
    icon: 'i-ri-megaphone-line',
    route: 'campaigns_ongoing_index',
    adminOnly: true,
  },
];

// SEM preset (Lucas 2026-08-10, revendo a decisão da madrugada): os atalhos
// nascem só com o "+" — cada pessoa monta do zero.
export const DEFAULT_DOCK_KEYS = [];

const normalizeItem = raw => {
  if (!raw || typeof raw !== 'object') return null;
  const key = String(raw.key || '');
  if (!key) return null;
  const kind = raw.kind === 'conversation' ? 'conversation' : 'destination';
  if (kind === 'conversation') {
    const id = Number(raw.conversationId);
    if (!Number.isFinite(id)) return null;
    return {
      kind,
      key,
      conversationId: id,
      label: String(raw.label || ''),
      thumbnail: String(raw.thumbnail || ''),
    };
  }
  // Destino desconhecido (catálogo mudou entre versões) é descartado em vez de
  // renderizar um botão que não navega pra lugar nenhum.
  const entry = DOCK_CATALOG.find(c => c.key === key);
  return entry ? { kind, key } : null;
};

export function useShortcutDock() {
  const { uiSettings, updateUISettings } = useUISettings();
  const { isAdmin } = useAdmin();
  const { t } = useI18n();

  const labelForKey = key => t(`DOCK.ITEMS.${key.toUpperCase()}`);

  const rawItems = computed(() => {
    const stored = uiSettings.value?.[DOCK_SETTINGS_KEY];
    if (!Array.isArray(stored)) {
      return DEFAULT_DOCK_KEYS.map(key => ({ kind: 'destination', key }));
    }
    return stored.map(normalizeItem).filter(Boolean);
  });

  // Item ADMIN sai do dock de quem deixou de ser admin — sem isso o atalho
  // continua na tela e leva a uma rota que o backend recusa.
  const items = computed(() =>
    rawItems.value.filter(item => {
      if (item.kind !== 'destination') return true;
      const entry = DOCK_CATALOG.find(c => c.key === item.key);
      return !entry?.adminOnly || isAdmin.value;
    })
  );

  const catalog = computed(() =>
    DOCK_CATALOG.filter(entry => !entry.adminOnly || isAdmin.value).map(
      entry => ({
        ...entry,
        label: labelForKey(entry.key),
        inDock: rawItems.value.some(
          item => item.kind === 'destination' && item.key === entry.key
        ),
      })
    )
  );

  const persist = next => {
    updateUISettings({ [DOCK_SETTINGS_KEY]: next });
  };

  const addItem = item => {
    const normalized = normalizeItem(item);
    if (!normalized) return;
    if (rawItems.value.some(existing => existing.key === normalized.key))
      return;
    persist([...rawItems.value, normalized]);
  };

  const removeItem = key => {
    persist(rawItems.value.filter(item => item.key !== key));
  };

  const addConversation = ({ id, label, thumbnail }) => {
    addItem({
      kind: 'conversation',
      key: `conversation:${id}`,
      conversationId: id,
      label,
      thumbnail,
    });
  };

  const displayLabel = item =>
    item.kind === 'conversation'
      ? item.label || t('DOCK.ITEMS.CONVERSATION')
      : labelForKey(item.key);

  const displayIcon = item =>
    item.kind === 'conversation'
      ? 'i-ri-chat-3-line'
      : DOCK_CATALOG.find(c => c.key === item.key)?.icon || 'i-ri-apps-line';

  return {
    items,
    catalog,
    addItem,
    addConversation,
    removeItem,
    displayLabel,
    displayIcon,
  };
}
